/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect } from 'react';

import { Box } from '@mui/material';
import { EncounterSession } from 'src/common/types/EncounterSession';
import SnackbarUtils from 'src/common/utils/SnackbarUtilsConfigurator';
import AIOutputSection from 'src/florence/components/SearchResultsContainerV2/componentsV2/AIOutputSection';
import CodesSection from 'src/florence/components/SearchResultsContainerV2/componentsV2/CodesSection';
import { useCodesExtended } from 'src/florence/hooks/useCodesExtended';
import { useNotes } from 'src/florence/hooks/useNotes';

import { usePrefetch } from '../../../common/apis/FlorenceApi';

import SurgeryEngineHeader from './SurgeryEngineHeader';

type Props = {
  initialEncounterSession: EncounterSession;
};

const SmartLaunchTabSurgeryEngine: React.FC<Props> = ({
  initialEncounterSession,
}) => {
  const [encounterSession, setEncounterSession] = useState(
    initialEncounterSession
  );

  // filters
  const [appHeaderHeight, setAppHeaderHeight] = useState(0);
  const [opNoteHeaderHeight, setOpNoteHeaderHeight] = useState(0);

  // ui
  const [isSaving, setIsSaving] = React.useState(false);
  const [isFinalizing, setIsFinalizing] = React.useState(false);

  const [finalizeOpen, setFinalizeOpen] = React.useState(false);

  // misc
  const getModifiers = usePrefetch('getModifiers');

  // note and codes
  const {
    currentNote,
    finalizeNote,
    updateNote,
    changeClinicNoteStatus,
    updateNoteText,
    refreshNote,
  } = useNotes(encounterSession.sheet ? [encounterSession.sheet] : []);

  const {
    codes,
    selectedCodes,
    isLoading,
    selectModifiers,
    selectMultiplier,
    refetchCodes,
    selectCode,
    selectAllCodes,
    clearSelectedCodes,
    addManualCode,
    incorrectCodeCombinations,
    flagCode,
    AIOutput,
  } = useCodesExtended(currentNote, currentNote?.scanText ?? '', 'scan', 20);

  // Run on page load
  React.useEffect(() => {
    getModifiers();
    refetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAll = (select: boolean) => {
    if (select) selectAllCodes();
    else clearSelectedCodes();
  };

  const onSaveNote = async () => {
    if (!selectedCodes.length) {
      return SnackbarUtils.error(
        'You need to have at least one code selected.'
      );
    }

    refetchCodes();
  };

  return (
    <Box
      className='responsive-padding light-blue-background'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: '100%',
      }}
    >
      <SurgeryEngineHeader
        note={currentNote}
        isLoading={isLoading || isSaving || isFinalizing}
        onSaveNote={() => null}
        saveIsLoading={isSaving}
        refreshResults={(text) => {
          if (currentNote) updateNoteText(currentNote, text);
        }}
        selectedCodes={selectedCodes}
        clearSelectedCodes={clearSelectedCodes}
        selectCode={selectCode}
        isFinalizing={isFinalizing}
        onFinalize={() => setFinalizeOpen(true)}
      />

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: {
            xs: '24px',
            sm: '40px',
            lg: '48px',
          },
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          marginTop: `${opNoteHeaderHeight}px`,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: incorrectCodeCombinations ? '100px' : '32px',
          }}
        >
          <CodesSection
            onSaveNote={onSaveNote}
            saveIsLoading={isSaving}
            allCodes={codes}
            selectedCodes={selectedCodes}
            note={currentNote}
            selectCode={selectCode}
            selectedAll={selectedAll}
            selectMultiplier={selectMultiplier}
            selectModifiers={selectModifiers}
            addManualCode={addManualCode}
            flagCode={flagCode}
            isLoading={isLoading}
            hasSelectedAll={
              codes.length !== 0 &&
              codes.filter((c) => !c.selected).length === 0
            }
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#ffffff',
            borderLeft: '1px solid  rgba(82, 86, 98, 0.20)',
            minHeight: {
              xs: '400px',
              md: `calc(100vh - ${opNoteHeaderHeight}px - ${appHeaderHeight}px + 20px)`,
            },
            maxWidth: {
              xs: '100%',
              md: '38%',
              xl: '36%',
            },
            padding: '48px 32px',
            marginBottom: incorrectCodeCombinations ? '76px' : '0px',
          }}
        >
          <AIOutputSection
            icd10={AIOutput?.icd10}
            modifiers={AIOutput?.modifiers}
            units={AIOutput?.units}
            grant={AIOutput?.grants_choice}
          />
        </Box>
      </Box>

      {/* <CCIAlertBottomBar open={incorrectCodeCombinations} /> */}
    </Box>
  );
};

export default SmartLaunchTabSurgeryEngine;
